import { get, isNil, upperCase } from 'lodash';
import React from 'react';
import { Avatar, Tag, Text } from 'src/@vymo/ui/atoms';
import { ReactComponent as TeamsIcon } from 'src/assets/navigation/teams.svg';
import Progress from '../components/progress';
import {
  PATH_ROW_METRIC_TARGET_ATTRIBUTES,
  PATH_TEAM_ROW_METRIC_TARGET_ATTRIBUTES,
} from '../constant';
import {
  getMetricData,
  getMetricProgressColor,
  getMetricProgressPercent,
} from '../dashboard/keyMetricsWidget/queries';
import styles from './index.module.scss';

export const keyMetricsColumns: any = [
  {
    title: 'Key Metric',
    field: 'title',
    canSort: false,
  },
  {
    title: 'Achievement',
    field: 'achievement',
    canSort: false,
    cell: (_, row) => {
      const { total, actual } = getMetricData(row);
      if (total || actual) {
        return (
          <div>
            <Text semiBold>{actual} </Text>
            {Boolean(total) && <Text>/ {total}</Text>}
          </div>
        );
      }
      return <Text>-</Text>;
    },
  },
  {
    title: 'Attained %',
    field: 'data.body.result.metric.target.progress_percentage',
    canSort: true,
    cell: (_, row) => {
      const progressPercentage = getMetricProgressPercent(row);
      const progressColor = getMetricProgressColor(row);

      if (!isNil(progressPercentage)) {
        return (
          <div
            style={{ color: progressColor }}
            className={styles.keyMetricsTable__percent}
          >
            <Progress color={progressColor} progress={progressPercentage} />
            {progressPercentage}%
          </div>
        );
      }
      return <Text>-</Text>;
    },
  },
  {
    title: 'Frequency',
    field: 'card.frequency',
    canSort: true,
    cell: (value) => {
      if (value) {
        return <Tag label={value} type="secondary" />;
      }
      return <Text>-</Text>;
    },
  },
  {
    title: 'Expected Target',
    field: 'data.body.result.metric.target.expected_progress',
    canSort: true,
  },
  {
    title: 'Shortfall',
    field: 'shortfall',
    canSort: false,
    cell: (_, row) => {
      const metricAttributes = get(row, PATH_ROW_METRIC_TARGET_ATTRIBUTES, []);

      const shortfall =
        metricAttributes.find((attr) => attr.label === 'Shortfall')?.value ||
        '-';
      return <Text>{shortfall}</Text>;
    },
  },
  {
    title: 'Status',
    field: 'data.body.result.metric.target.progress_type',
    canSort: false,
    cell: (value, row) => {
      const progressColor = getMetricProgressColor(row);
      if (value) {
        return (
          <div
            style={{
              background: `${progressColor}1A`,
              color: progressColor,
            }}
            className={styles.keyMetricsTable__status}
          >
            {upperCase(value)}
          </div>
        );
      }
      return <Text>-</Text>;
    },
  },
  {
    title: 'Last Updated',
    field: 'data.body.result.metric.last_updated_date',
    input: { type: 'date' },
    canSort: true,
  },
];

export const teamBreakdownColumns: any = [
  {
    title: 'Team Member',
    field: 'title',
    canSort: false,
    cell: (_, row) => {
      const name = row?.body?.result?.group_by_key?.name;
      const subordinatesCount = row?.body?.result?.subordinatesCount;

      return (
        <div className={styles.details__teamBreakdown__table__teamMember}>
          <Avatar
            background="var(--background-default)"
            text={name?.charAt?.(0) || ''}
            size="small"
          />
          <Text>{name}</Text>
          {Boolean(subordinatesCount) && (
            <>
              {' . '}
              <Tag>
                <TeamsIcon /> {subordinatesCount}
              </Tag>
            </>
          )}
        </div>
      );
    },
  },
  {
    title: 'Achievement',
    field: 'achievement',
    canSort: false,
    cell: (_, row) => {
      const { total, actual } = getMetricData({ data: row });
      if (total || actual) {
        return (
          <div>
            <Text semiBold>{actual} </Text>
            {Boolean(total) && <Text>/ {total}</Text>}
          </div>
        );
      }
      return <Text>-</Text>;
    },
  },
  {
    title: 'Attained %',
    field: 'data.body.result.metric.target.progress_percentage',
    canSort: true,
    cell: (_, row) => {
      const progressPercentage = getMetricProgressPercent({ data: row });

      const progressColor = getMetricProgressColor({ data: row });

      if (!isNil(progressPercentage)) {
        return (
          <div
            style={{ color: progressColor }}
            className={styles.keyMetricsTable__percent}
          >
            <Progress color={progressColor} progress={progressPercentage} />
            {progressPercentage}%
          </div>
        );
      }
      return <Text>-</Text>;
    },
  },
  {
    title: 'Expected Target',
    field: 'body.result.metric.target.expected_progress',
    canSort: true,
  },
  {
    title: 'Shortfall',
    field: 'shortfall',
    canSort: false,
    cell: (_, row) => {
      const metricAttributes = get(
        row,
        PATH_TEAM_ROW_METRIC_TARGET_ATTRIBUTES,
        [],
      );

      const shortfall =
        metricAttributes.find((attr) => attr.label === 'Shortfall')?.value ||
        '-';
      return <Text>{shortfall}</Text>;
    },
  },
  {
    title: 'Status',
    field: 'body.result.metric.target.progress_type',
    canSort: false,
    cell: (value, row) => {
      const progressColor = getMetricProgressColor({ data: row });
      if (value) {
        return (
          <div
            style={{
              background: `${progressColor}1A`,
              color: progressColor,
            }}
            className={styles.keyMetricsTable__status}
          >
            {upperCase(value)}
          </div>
        );
      }
      return <Text>-</Text>;
    },
  },
];

export const chartMargins = {
  top: 32,
  right: 16,
  bottom: 28,
};

export const DAY_IN_MS = 24 * 60 * 60 * 1000;
export const MIN_DAYS = 30;
export const DEFAULT_TARGET = 300000;

export const genericParams = (userId, card, goalId, filters = {}) => ({
  goal_definition_code: goalId,
  filters: JSON.stringify({
    ...filters,
    frequency: card?.frequency,
    start_date: card?.date_range?.start,
    user_id: userId,
  }),
});
